import * as React from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import { memo, useState, useEffect, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
    useProductItemDataQuery,
    useUpdateProductItemMutation,
} from 'store/slices/product/product-item';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { ItemCreatRequest } from 'types/product/product-item';
import ItemGoldGem from 'component/product-item/item-gold-gem';
import ItemCard from 'component/product-item/item-card';
import ItemNoAds from 'component/product-item/item-no-ads';
import ItemSubscribe from 'component/product-item/item-subscribe';
import DialogIconClose from 'component/dialog-icon-close';
import colors from 'assets/theme/base/colors';
import rgba from 'assets/theme/function/rgba';
import { ProductItemTypeEnum } from 'types/product/product-item-type';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    setOpenDialogEditProductItem: React.Dispatch<React.SetStateAction<boolean>>;
    getItemList: () => void;
    productItemId: string;
}

const EditProductItem: React.FC<Props> = memo((props: Props) => {
    const { getItemList, setOpenDialogEditProductItem, productItemId } = props;
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<ItemCreatRequest>();

    const [selectedCategory, setSelectedCategory] = useState('');
    const selectedValue = useWatch({ control, name: 'itemType' });
    const formRef = useRef<HTMLFormElement>(null);

    const [updateProductItemApi, { isSuccess, error, isError }] =
        useUpdateProductItemMutation();

    const { data, refetch: refetchProductId } =
        useProductItemDataQuery(productItemId);

    const dispatch = useAppDispatch();

    const getCardCategoryList = [
        {
            label: 'Gold',
            component: (
                <ItemGoldGem data={data} errors={errors} control={control} />
            ),
            key: ProductItemTypeEnum.GOLD,
        },
        {
            label: 'Gem',
            component: (
                <ItemGoldGem data={data} errors={errors} control={control} />
            ),
            key: ProductItemTypeEnum.GEM,
        },
        {
            label: 'Card',
            component: <ItemCard data={data} control={control} />,
            key: ProductItemTypeEnum.CARD,
        },
        {
            label: 'No Ads',
            component: (
                <ItemNoAds data={data} errors={errors} control={control} />
            ),
            key: ProductItemTypeEnum.NO_ADS,
        },
        {
            label: 'Subscribe',
            component: (
                <ItemSubscribe data={data} errors={errors} control={control} />
            ),
            key: ProductItemTypeEnum.SUBSCRIBE,
        },
    ];

    const onSubmit = (values: ItemCreatRequest) => {
        updateProductItemApi({ ...values, productId: productItemId ?? '' });
    };

    const closeDialog = () => {
        setOpenDialogEditProductItem(false);
        reset();
    };

    useEffect(() => {
        if (data?.itemType) {
            reset({
                itemType: data.itemType,
            });
            setSelectedCategory(data.itemType);
        }
    }, [data, reset]);

    useEffect(() => {
        setSelectedCategory(selectedValue || '');
    }, [selectedValue]);

    useEffect(() => {
        if (isSuccess) {
            closeDialog();
            getItemList();
            refetchProductId();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
        if (isSuccess) {
            dispatch(
                openSnackbar({
                    message: 'Product Price Update',
                    severity: 'info',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
            closeDialog();
        }
    }, [isError, isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    EDIT NEW ITEM
                </DialogTitle>
                <DialogIconClose handleClose={closeDialog} />
            </Box>

            <DialogContent>
                <Box
                    component="form"
                    ref={formRef}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box display={'flex'} pt={pxToRem(15)}>
                        <FormControl fullWidth>
                            <InputLabel id="product-type-label">
                                Product Type
                            </InputLabel>
                            <Controller
                                name="itemType"
                                control={control}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        labelId="product-type-label"
                                        name={name}
                                        value={value || ''}
                                        label="Product Type"
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setSelectedCategory(newValue);
                                            onChange(newValue);
                                        }}
                                    >
                                        {getCardCategoryList.map((category) => (
                                            <MenuItem
                                                key={category.key}
                                                value={category.key}
                                            >
                                                {category.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Box>

                    {selectedCategory && (
                        <Box width={'100%'} pt={pxToRem(15)}>
                            {
                                getCardCategoryList.find(
                                    (category) =>
                                        category.key === selectedCategory,
                                )?.component
                            }
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                >
                    Product Item Update
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeDialog()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Product Item!'}
            />
        </>
    );
});

export default EditProductItem;
